import { Badge, Button, Card, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";

type Sorumluluk = { domain: string; description: string; count: number };

type Organizasyon = {
  tenantId: string;
  name: string;
  blockedReason: "last_admin" | "blocking_responsibility" | "not_active" | null;
  openResponsibilities: Sorumluluk[];
};

type Onizleme = { canClose: boolean; consequences: string[]; organizations: Organizasyon[] };

const ENGEL_METNI: Record<NonNullable<Organizasyon["blockedReason"]>, string> = {
  last_admin: "Bu organizasyonun son yöneticisisiniz.",
  blocking_responsibility: "Bu organizasyonda devredilemeyen açık işleriniz var.",
  not_active: "Bu organizasyondaki üyeliğiniz aktif değil.",
};

/**
 * HESAP KAPATMA (CLOS-69 `[049]`).
 *
 * ⚠ Ne ön izleme ne de kapatma ekranda vardı: kullanıcı hesabını kendisi kapatamıyordu.
 *
 * ⚠ Sonuçlar ve ORGANİZASYON BAŞINA açık işler kapatmadan önce okunuyor. Toplam bir sayı
 * göstermek yeterli olmazdı: kullanıcının hangi organizasyonda ne bıraktığını bilmesi
 * gerekiyor, çünkü çözümü orada.
 *
 * ⚠ Engelli bir organizasyon varsa kapatma SUNULMUYOR ve sebep o organizasyonun yanında
 * duruyor — "kapatamıyorsunuz" demek yetmez, nerede ne yapılacağı görünmeli.
 *
 * ⚠ Parola ve açık onay isteniyor (2FA etkinse sunucu ayrıca ikinci faktör arıyor). Geri
 * alınamaz bir işlemde tek tıklama, kullanıcıya dönüşü olmayan bir hata yaptırırdı.
 */
export function AccountClosureSection() {
  const toast = useToast();
  const [onayAcik, setOnayAcik] = useState(false);
  const [parola, setParola] = useState("");
  const [kod, setKod] = useState("");
  const [hata, setHata] = useState<string | null>(null);

  const sorgu = useQuery({
    queryKey: ["profil", "kapatma-onizleme"],
    queryFn: async () => (await api<Onizleme>("/api/profile/closure-preview")).data,
  });

  const kapat = useMutation({
    mutationFn: async () => {
      const govde: Record<string, unknown> = { currentPassword: parola, confirm: true };
      /** Kod ancak GİRİLDİYSE gövdeye giriyor: boş metin göndermek şemaya takılırdı. */
      if (kod.trim().length > 0) govde.code = kod.trim();
      return (await api("/api/profile/closure", { method: "POST", body: JSON.stringify(govde) }))
        .data;
    },
    onSuccess: () => {
      toast.success("Hesabınız kapatıldı");
      /** Oturum sunucuda düştü; tam yenileme kullanıcıyı giriş ekranına götürüyor. */
      window.location.assign("/login");
    },
    onError: (error) => {
      setHata(error instanceof ApiRequestError ? error.message : "Hesap kapatılamadı");
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Hesabı Kapat">
        <div role="status" aria-label="Kapatma bilgisi yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  const onizleme = sorgu.data;
  const engelli = (onizleme?.organizations ?? []).filter((o) => o.blockedReason !== null);

  return (
    <Card title="Hesabı Kapat">
      <div className="flex flex-col gap-3">
        {engelli.length > 0 ? (
          <p role="alert" className="text-sm text-danger">
            {engelli.map((o) => `${o.name}: ${ENGEL_METNI[o.blockedReason!]}`).join(" ")}
          </p>
        ) : null}

        <ul className="flex flex-col gap-1 text-sm text-fg">
          {(onizleme?.consequences ?? []).map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>

        {(onizleme?.organizations ?? []).length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-fg-muted">Üyeliğiniz olan organizasyonlar</p>
            <ul className="flex flex-col gap-2">
              {(onizleme?.organizations ?? []).map((o) => (
                <li key={o.tenantId} className="flex flex-col gap-1">
                  <span className="flex items-center gap-2 text-sm text-fg-strong">
                    {o.name}
                    {o.blockedReason ? <Badge tone="danger">Engelliyor</Badge> : null}
                  </span>
                  {o.openResponsibilities.map((s) => (
                    <span key={`${o.tenantId}-${s.description}`} className="text-sm text-fg-soft">
                      {s.description}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {hata ? (
          <p role="alert" className="text-sm text-danger">
            {hata}
          </p>
        ) : null}

        {onizleme?.canClose ? (
          onayAcik ? (
            <div className="flex flex-col gap-3">
              <FormField label="Şifreniz" required>
                <Input
                  aria-label="Şifreniz"
                  type="password"
                  autoComplete="current-password"
                  value={parola}
                  onChange={(e) => setParola(e.target.value)}
                />
              </FormField>
              {/* 2FA'sı olan kullanıcı için; olmayanda sunucu bu alanı zaten aramıyor. */}
              <FormField label="Doğrulama kodu (2FA açıksa)">
                <Input
                  aria-label="Doğrulama kodu (2FA açıksa)"
                  value={kod}
                  autoComplete="one-time-code"
                  onChange={(e) => setKod(e.target.value)}
                />
              </FormField>
              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setOnayAcik(false)}>
                  Vazgeç
                </Button>
                <Button
                  type="button"
                  loading={kapat.isPending}
                  disabled={parola === ""}
                  onClick={() => kapat.mutate()}
                >
                  Kapatmayı onayla
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={() => setOnayAcik(true)}>
                Hesabımı kapat
              </Button>
            </div>
          )
        ) : null}
      </div>
    </Card>
  );
}
